import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { XMarkIcon } from '@heroicons/react/24/outline';

const RESOURCE_LABELS = {
  applicant: 'Applicants',
  new_leads_page: 'New Leads Page',
  job: 'Jobs',
  user: 'User Management',
  role: 'Role Management',
  permission: 'Permission Management'
};

const UserForm = ({ user, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    telegramUserId: '',
    permissionIds: [],
    isActive: true
  });
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      fetchPermissions();
      setFormData({
        name: user.name || '',
        email: user.email || '',
        password: '',
        telegramUserId: user.telegramUserId || '',
        permissionIds: user.Permissions ? user.Permissions.map(p => p.id) : [],
        isActive: user.isActive !== undefined ? user.isActive : true
      });
    } else {
      fetchPermissions();
    }
  }, [user]);

  const fetchPermissions = async () => {
    try {
      const response = await axios.get('/api/permissions');
      setPermissions(response.data);
    } catch (err) {
      console.error('Error fetching permissions:', err);
    }
  };

  const groupedPermissions = permissions.reduce((groups, perm) => {
    const key = perm.resource;
    if (!groups[key]) groups[key] = [];
    groups[key].push(perm);
    return groups;
  }, {});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePermissionChange = (permId) => {
    setFormData(prev => ({
      ...prev,
      permissionIds: prev.permissionIds.includes(permId)
        ? prev.permissionIds.filter(id => id !== permId)
        : [...prev.permissionIds, permId]
    }));
  };

  const handleToggleGroup = (resource) => {
    const groupIds = groupedPermissions[resource].map(p => p.id);
    const allSelected = groupIds.every(id => formData.permissionIds.includes(id));
    setFormData(prev => ({
      ...prev,
      permissionIds: allSelected
        ? prev.permissionIds.filter(id => !groupIds.includes(id))
        : [...new Set([...prev.permissionIds, ...groupIds])]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const payload = { ...formData };
      if (!payload.password) delete payload.password;
      if (!payload.email) delete payload.email;

      if (user) {
        await axios.put(`/api/users/${user.id}`, payload);
      } else {
        if (!payload.password) {
          setError('Password is required for new users');
          setLoading(false);
          return;
        }
        await axios.post('/api/users', payload);
      }
      onClose();
    } catch (error) {
      setError(error.response?.data?.message || 'Error saving user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white mb-10">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {user ? 'Edit User' : 'Add User'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-800">{error}</div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Telegram ID
            </label>
            <input
              type="text"
              name="telegramUserId"
              value={formData.telegramUserId}
              onChange={handleChange}
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder="Telegram numeric user ID"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password {!user && <span className="text-red-500">*</span>}
            </label>
            <input
              type="password"
              name="password"
              required={!user}
              value={formData.password}
              onChange={handleChange}
              placeholder={user ? 'Leave blank to keep current password' : ''}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {user && permissions.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
              <div className="border border-gray-300 rounded-md divide-y divide-gray-200 max-h-64 overflow-y-auto">
                {Object.entries(groupedPermissions).map(([resource, perms]) => {
                  const groupIds = perms.map(p => p.id);
                  const allSelected = groupIds.every(id => formData.permissionIds.includes(id));
                  const someSelected = groupIds.some(id => formData.permissionIds.includes(id));

                  return (
                    <div key={resource} className="p-3">
                      <label className="flex items-center mb-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                          onChange={() => handleToggleGroup(resource)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm font-semibold text-gray-800">
                          {RESOURCE_LABELS[resource] || resource}
                        </span>
                      </label>
                      <div className="ml-6 grid grid-cols-2 gap-1">
                        {perms.map(perm => (
                          <label key={perm.id} className="flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.permissionIds.includes(perm.id)}
                              onChange={() => handlePermissionChange(perm.id)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="ml-2 text-sm text-gray-600">{perm.description || perm.action}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-700">Active</span>
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : user ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserForm;

